import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching ALL subscription plans
export const fetchAllSubscriptionPlans = createAsyncThunk(
  "subscriptionPlan/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.SUBSCRIPTION_PLAN.SELECT_ALL,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ SubscriptionPlans API Error:",
        error.response?.status,
        error.response?.data,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch subscription plans",
      );
    }
  },
);

// Thunk for fetching subscription plan by ID
export const fetchSubscriptionPlanById = createAsyncThunk(
  "subscriptionPlan/fetchById",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.SUBSCRIPTION_PLAN.SELECT}/${planId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Subscription plan not found",
      );
    }
  },
);

// Thunk for adding a new subscription plan
export const addSubscriptionPlan = createAsyncThunk(
  "subscriptionPlan/add",
  async (planData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.SUBSCRIPTION_PLAN.INSERT,
        planData,
      );
      dispatch(fetchAllSubscriptionPlans());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add subscription plan",
      );
    }
  },
);

// Thunk for updating an existing subscription plan
export const updateSubscriptionPlan = createAsyncThunk(
  "subscriptionPlan/update",
  async (planData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.SUBSCRIPTION_PLAN.UPDATE,
        planData,
      );
      dispatch(fetchAllSubscriptionPlans());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update subscription plan",
      );
    }
  },
);

// Thunk for deactivating a subscription plan (soft delete)
export const deactivateSubscriptionPlan = createAsyncThunk(
  "subscriptionPlan/deactivate",
  async (planId, { rejectWithValue, dispatch }) => {
    try {
      // Optimistic update via reducer pattern (safe)
      dispatch({
        type: "subscriptionPlan/setPlanInactiveOptimistic",
        payload: planId,
      });

      await axiosInstance.delete(
        `${API_ENDPOINTS.SUBSCRIPTION_PLAN.DELETE}?id=${planId}`,
      );

      // Refetch to sync with backend (even if hard delete)
      dispatch(fetchAllSubscriptionPlans());
      return planId;
    } catch (error) {
      console.error(
        "❌ Backend DELETE failed:",
        error.response?.status,
        error.response?.data,
      );

      // Revert optimistic via dispatch (safe)
      dispatch({
        type: "subscriptionPlan/revertOptimisticDeactivate",
        payload: planId,
      });

      return rejectWithValue(
        error.response?.data?.message ||
          `Backend error ${error.response?.status || "unknown"}`,
      );
    }
  },
);

const subscriptionPlanSlice = createSlice({
  name: "subscriptionPlan",
  initialState: {
    plansList: [],
    pagination: null,
    selectedPlan: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearSelectedSubscriptionPlan: (state) => {
      state.selectedPlan = null;
    },
    // Optimistic updates (safe Redux pattern)
    setPlanInactiveOptimistic: (state, action) => {
      const planIndex = state.plansList.findIndex(
        (p) => p.planId === action.payload,
      );
      if (planIndex !== -1) {
        state.plansList[planIndex].isDeleted = true;
        state.plansList[planIndex].statusText = "Inactive";
      }
    },
    revertOptimisticDeactivate: (state, action) => {
      const planIndex = state.plansList.findIndex(
        (p) => p.planId === action.payload,
      );
      if (planIndex !== -1 && state.plansList[planIndex].isDeleted === true) {
        state.plansList[planIndex].isDeleted = false;
        state.plansList[planIndex].statusText = "Active";
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Plans - FIXED
      .addCase(fetchAllSubscriptionPlans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllSubscriptionPlans.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.plansList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllSubscriptionPlans.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch subscription plans";
        console.error("❌ SubscriptionPlans API Failed:", action.payload);
      })
      // Fetch By ID
      .addCase(fetchSubscriptionPlanById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlanById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedPlan = action.payload.data;
      })
      .addCase(fetchSubscriptionPlanById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Plan
      .addCase(addSubscriptionPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addSubscriptionPlan.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addSubscriptionPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Plan
      .addCase(updateSubscriptionPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSubscriptionPlan.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateSubscriptionPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Deactivate Plan (Soft Delete)
      .addCase(deactivateSubscriptionPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deactivateSubscriptionPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        const planIndex = state.plansList.findIndex(
          (p) => p.planId === action.payload,
        );
        if (planIndex !== -1) {
          state.plansList[planIndex].isDeleted = true;
          state.plansList[planIndex].statusText = "Inactive";
        }
      })
      .addCase(deactivateSubscriptionPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        console.error("❌ Deactivation failed:", action.payload);
      });
  },
});

export const { clearSelectedSubscriptionPlan } = subscriptionPlanSlice.actions;
export default subscriptionPlanSlice.reducer;
